import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { format, getYear, startOfYear, endOfYear, eachYearOfInterval, subYears } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar, TrendingUp, TrendingDown, BarChart3, PieChart as PieChartIcon, ArrowUpDown } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CustomTooltipProps, TooltipPayload } from "@/types/recharts";

interface TransactionWithCategory {
  id: string;
  tipo: "entrada" | "saida";
  valor: number;
  data: string;
  foi_paga: boolean;
  ignorar_transacao: boolean;
  categoria?: {
    nome: string;
    cor: string;
  };
}

interface YearlyComparisonReportProps {
  transactions: TransactionWithCategory[];
  selectedDate?: Date;
}

interface YearlyData {
  year: string;
  receitas: number;
  despesas: number;
  saldo: number;
  [key: string]: string | number;
}

interface CategoryYearData {
  category: string;
  [key: string]: string | number;
}

export function YearlyComparisonReport({ transactions, selectedDate }: YearlyComparisonReportProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'categories'>('overview');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [yearsToShow, setYearsToShow] = useState(3);
  // Iniciar com showAllYears como true para sempre mostrar todos os anos
  const [showAllYears, setShowAllYears] = useState(true);

  const yearlyData = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return { overviewData: [], categoryData: [], categories: [], insights: [] };
    }

    // Usar new Date() para obter o ano atual, ignorando o selectedDate
    const currentYear = getYear(new Date());
    let years: number[];
    
    if (showAllYears) {
      // Determinar automaticamente o ano mais antigo baseado nas transações
      const validTransactions = transactions.filter(t => !t.ignorar_transacao);
      let startYear = currentYear;
      
      if (validTransactions.length > 0) {
        const oldestTransaction = validTransactions.reduce((oldest, transaction) => {
          const transactionYear = getYear(new Date(transaction.data));
          return transactionYear < oldest ? transactionYear : oldest;
        }, currentYear);
        startYear = oldestTransaction;
      }
      
      const yearCount = currentYear - startYear + 1;
      years = Array.from({ length: yearCount }, (_, i) => startYear + i);
    } else {
      // Lógica original para número limitado de anos
      const startYear = currentYear - (yearsToShow - 1);
      years = Array.from({ length: yearsToShow }, (_, i) => startYear + i);
    }

    // Filtrar transações válidas (para uso geral)
    const validTransactions = transactions.filter(t => !t.ignorar_transacao);

    // Dados gerais por ano
    const overviewData: YearlyData[] = years.map(year => {
      const yearStart = startOfYear(new Date(year, 0, 1));
      const yearEnd = endOfYear(new Date(year, 0, 1));
      
      const yearTransactions = validTransactions.filter(t => {
        const transactionDate = new Date(t.data);
        return transactionDate >= yearStart && transactionDate <= yearEnd;
      });
      
      const receitas = yearTransactions
        .filter(t => t.tipo === 'entrada' && t.foi_paga)
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      const despesas = yearTransactions
        .filter(t => t.tipo === 'saida')
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      return {
        year: year.toString(),
        receitas,
        despesas,
        saldo: receitas - despesas
      };
    });

    // Obter todas as categorias
    const allCategories = Array.from(
      new Set(
        validTransactions
          .map(t => t.categoria?.nome || 'Sem categoria')
          .filter(Boolean)
      )
    ).sort();

    // Dados por categoria e ano
    const categoryData: CategoryYearData[] = allCategories.map(categoryName => {
      const categoryRow: CategoryYearData = { category: categoryName };
      
      years.forEach(year => {
        const yearStart = startOfYear(new Date(year, 0, 1));
        const yearEnd = endOfYear(new Date(year, 0, 1));
        
        const categoryTotal = validTransactions
          .filter(t => {
            const transactionDate = new Date(t.data);
            return (
              transactionDate >= yearStart &&
              transactionDate <= yearEnd &&
              (t.categoria?.nome || 'Sem categoria') === categoryName &&
              (selectedCategory === 'all' || 
               (selectedCategory === 'receitas' && t.tipo === 'entrada' && t.foi_paga) ||
               (selectedCategory === 'despesas' && t.tipo === 'saida'))
            );
          })
          .reduce((sum, t) => sum + Number(t.valor), 0);
        
        categoryRow[year.toString()] = categoryTotal;
      });
      
      return categoryRow;
    });

    // Calcular insights
    const insights = [
      {
        title: 'Crescimento de Receitas',
        value: overviewData.length > 1 && overviewData[0].receitas !== 0 ? 
          ((overviewData[overviewData.length - 1].receitas - overviewData[0].receitas) / overviewData[0].receitas * 100) : 0,
        type: 'percentage' as const,
        trend: 'up' as const
      },
      {
        title: 'Variação de Despesas',
        value: overviewData.length > 1 && overviewData[0].despesas !== 0 ? 
          ((overviewData[overviewData.length - 1].despesas - overviewData[0].despesas) / overviewData[0].despesas * 100) : 0,
        type: 'percentage' as const,
        trend: 'down' as const
      },
      {
        title: 'Melhor Ano (Saldo)',
        value: Math.max(...overviewData.map(d => d.saldo)),
        type: 'currency' as const,
        year: overviewData.find(d => d.saldo === Math.max(...overviewData.map(d => d.saldo)))?.year
      },
      {
        title: 'Média Anual de Receitas',
        value: overviewData.reduce((sum, d) => sum + d.receitas, 0) / overviewData.length,
        type: 'currency' as const
      }
    ];

    return { overviewData, categoryData, categories: allCategories, insights };
  }, [transactions, yearsToShow, selectedCategory, showAllYears]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: TooltipPayload, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.dataKey === 'receitas' ? 'Receitas' : 
               entry.dataKey === 'despesas' ? 'Despesas' : 
               entry.dataKey === 'saldo' ? 'Saldo' : entry.dataKey}: {formatCurrency(entry.value as number)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const pieData = useMemo(() => {
    if (viewMode !== 'categories' || !yearlyData.categoryData.length) return [];
    
    // Usar o ano atual em vez do selectedDate para manter consistência
    const currentYear = new Date().getFullYear().toString();
    return yearlyData.categoryData
      .map(item => ({
        name: item.category,
        value: Number(item[currentYear]) || 0
      }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [yearlyData.categoryData, viewMode]);

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))'
  ];

  return (
    <div className="space-y-6">
      {/* Insights Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {yearlyData.insights.map((insight, index) => (
          <Card key={insight.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {insight.title}
              </CardTitle>
              <div className="p-2 rounded-md bg-primary/10">
                {insight.trend === 'up' ? (
                  <TrendingUp className="h-4 w-4 text-success" />
                ) : insight.trend === 'down' ? (
                  <TrendingDown className="h-4 w-4 text-destructive" />
                ) : (
                  <Calendar className="h-4 w-4 text-primary" />
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-base font-bold">
                {insight.type === 'currency' 
                  ? formatCurrency(insight.value)
                  : formatPercentage(insight.value)
                }
              </div>
              {insight.year && (
                <p className="text-xs text-muted-foreground mt-1">
                  Ano: {insight.year}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'overview' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('overview')}
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Visão Geral
          </Button>
          <Button
            variant={viewMode === 'categories' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('categories')}
          >
            <PieChartIcon className="h-4 w-4 mr-2" />
            Por Categoria
          </Button>
        </div>
        
        <div className="flex gap-2 items-center">
          <Select 
            value={showAllYears ? "all" : yearsToShow.toString()} 
            onValueChange={(value) => {
              if (value === "all") {
                setShowAllYears(true);
              } else {
                setShowAllYears(false);
                setYearsToShow(Number(value));
              }
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2 anos</SelectItem>
              <SelectItem value="3">3 anos</SelectItem>
              <SelectItem value="4">4 anos</SelectItem>
              <SelectItem value="5">5 anos</SelectItem>
              <SelectItem value="all">Todos os anos</SelectItem>
            </SelectContent>
          </Select>
          
          {viewMode === 'categories' && (
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="receitas">Receitas</SelectItem>
                <SelectItem value="despesas">Despesas</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {viewMode === 'overview' ? (
        /* Overview Charts */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Receitas vs Despesas */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Receitas vs Despesas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyData.overviewData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="year" 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(value) => {
                        if (value >= 1000) {
                          return `R$ ${(value / 1000).toFixed(0)}k`;
                        }
                        return `R$ ${value}`;
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="receitas" fill="hsl(var(--success))" name="Receitas" />
                    <Bar dataKey="despesas" fill="hsl(var(--destructive))" name="Despesas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Saldo Anual */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowUpDown className="h-5 w-5" />
                Saldo Anual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyData.overviewData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="year" 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(value) => {
                        if (Math.abs(value) >= 1000) {
                          return `R$ ${(value / 1000).toFixed(0)}k`;
                        }
                        return `R$ ${value}`;
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="saldo" 
                      fill="#3B82F6"
                      name="Saldo"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Categories View */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Comparison */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Comparativo por Categoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearlyData.categoryData.slice(0, 8)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="category" 
                      tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      tickFormatter={(value) => {
                        if (value >= 1000) {
                          return `R$ ${(value / 1000).toFixed(0)}k`;
                        }
                        return `R$ ${value}`;
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {yearlyData.overviewData.map((_, index) => (
                      <Bar 
                        key={yearlyData.overviewData[index].year}
                        dataKey={yearlyData.overviewData[index].year}
                        fill={COLORS[index % COLORS.length]}
                        name={yearlyData.overviewData[index].year}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Current Year Distribution */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <PieChartIcon className="h-5 w-5" />
                Distribuição {new Date().getFullYear()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}